import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime, filter } from 'rxjs/operators';

import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

import { WindowService } from 'src/shared/providers/window.service';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { OptimizerSectionComponent } from '../calendar/optimizer-section.component';
import { OptimizationSectionComponent } from './optimization-section.component';
import { RentalTypesComponent } from './rental-types.component';
import { SeasonalCancellationComponent } from './seasonal-cancellation.component';
import { PlatformPillsComponent } from './platform-pills.component';
import { RankTrackerSettingsComponent } from './rank-tracker-settings.component';
import { MOCK_ADDRESSES, MockAddress } from '../mock-property';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { RentalStrategyService } from 'src/shared/services/rental-strategy.service';
import {
  CompListing, CompSummary, MidLongTerm,
  ChannelListingRow, getComps, summarizeComps, getChannelRows, emptyChannelRows, formatCents,
} from './mid-long-mocks';
import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { Address } from 'src/shared/services/addresses-api.service';
import { AddressSetting, LISTING_SETTING_CATEGORY, ListingOptimizationSettingsService, OPTIMIZER_SETTING_CATEGORY, PlatformSyncStatus } from 'src/shared/services/listing-optimization-settings.service';
import { ListingContentSummary, listingContentSummary } from 'src/pages/property-listings/listing-content.model';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { SyncStatusDotComponent, SyncDotInfo } from 'src/shared/components/sync-status-dot/sync-status-dot.component';
import { ListingCalendarService } from 'src/shared/services/listing-calendar.service';
import { formatPlatformName } from 'src/shared/constants/listing-platforms';
import { OptimizerSaveBarService, SaveBarOwner } from 'src/shared/services/optimizer-save-bar.service';
import { RentalModesService } from 'src/shared/services/rental-modes.service';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { Util } from 'src/shared/util/util';
import { ADVANCE_NOTICE_DAYS, CancellationPolicy, OptimizerSettings, OptimizerValidationError, PREPARATION_TIME_NIGHTS, Platform, RentalTypesState, SeasonalWindowState, aggregateSyncStatus, failedSyncPlatforms, pendingSyncPlatforms, syncedSyncPlatforms, unsupportedSyncPlatforms, coveragePlatforms, buildDiscountSyncNote, changedSettingKeys, connectedPlatformsFrom, defaultSettings, fromSettings, noListingWarning, numOrNull, toEntries, validateOptimizerSettings, validatePriceBounds, validateSeasonalCancellation, validateStayLength, withoutBuilderOwnedKeys } from './booking-settings.model';
import { Client } from 'src/providers/client/client';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

type SectionKey = 'price' | 'strategy' | 'channel' | 'rank' | 'discount' | 'minStay' | 'listing'
  | 'mlComps' | 'mlTarget' | 'mlListing';

// Discrete, individually-matchable subsection inside the page. Each id maps
// 1:1 to a `<div class="bs-card">` (or `<div class="bs-discount">`) block in
// the template, plus the top-of-page calendar and the two whole-section units
// (`strategy` / `rank-tracker`) that have no inner subdivisions.
export type SubsectionId =
  | 'calendar' | 'strategy' | 'price-range'
  | 'channel-instant-book' | 'channel-non-refundable' | 'channel-seasonal-cancellation'
  | 'rank-tracker'
  | 'discount-weekly' | 'discount-monthly' | 'discount-last-minute' | 'discount-far-out'
  | 'discount-member-only' | 'discount-mobile' | 'discount-flash'
  | 'minstay-checkin-checkout' | 'minstay-scheduling'
  | 'listing-content' | 'listing-pet-friendly' | 'listing-cancellation-policy'
  // ---- Longer-term optimizer group ----
  | 'ml-comps' | 'ml-target-rent' | 'ml-listing-optimization';

@Component({
  selector: 'app-revenue-booking-settings-page',
  templateUrl: './booking-settings.page.html',
  styleUrls: ['./booking-settings.page.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
    OptimizerSectionComponent,
    OptimizationSectionComponent,
    RentalTypesComponent,
    SeasonalCancellationComponent,
    RankTrackerSettingsComponent,
    SyncStatusDotComponent,
    PlatformPillsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BookingSettingsPage implements OnInit, OnDestroy {
  readonly windowService = inject(WindowService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly addressesQueries = inject(AddressesQueriesService);
  private readonly listingSettings = inject(ListingOptimizationSettingsService);
  private readonly listingCalendar = inject(ListingCalendarService);
  private readonly util = inject(Util);
  private readonly client = inject(Client);
  private readonly saveBar = inject(OptimizerSaveBarService);
  private readonly rentalModes = inject(RentalModesService);
  private readonly urlState = inject(UrlStateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly storage = inject(TidyStorage);

  @ViewChild(OptimizerSectionComponent) optimizerSection?: OptimizerSectionComponent;

  /**
   * Set when the listing builder embeds the short-term settings for one property.
   * The host owns the property, the Save button and the fields it already shows
   * (pets, cancellation policy, security deposit), so this page drops its header,
   * calendar, search, long-term tab and the app-root save bar.
   */
  readonly embeddedAddressId = input<number | null>(null);
  readonly embedded = computed(() => this.embeddedAddressId() !== null);

  // The app-root save bar, while this page is the one in front. Gated on `isActive`
  // so it never lingers over another page after we navigate away (Ionic keeps this
  // page in the DOM), and on a real address. Claimed only by the full page; the
  // listing builder's embedded copy rides the builder's own bar.
  private readonly saveBarOwner: SaveBarOwner = {
    visible: computed(() => this.isActive() && this.isDirty() && !this.isDemo && !this.noRealAddress()),
    saving: computed(() => this.isActive() && this.isSaving()),
    save: () => { void this.saveChanges(); },
    discard: () => this.discardChanges(),
  };

  constructor() {
    // On desktop the listing builder opens as a panel over this page, so the page never
    // re-enters; without this its next save writes back what the builder just changed.
    this.rightSidePanelService.afterPanelClosed()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (!this.embedded() && this.isActive()) { this.reloadIfClean(); }
      });
  }

  /**
   * First-load gate. While `false`, the page shows the skeleton (which
   * carries the float-in entrance animation). Flips to `true` once and the
   * real content replaces every skeleton in place, instantly and with no
   * animation. Never flips back, so route-reuse reattachment never reshows
   * the skeleton.
   */
  readonly isLoaded = signal<boolean>(false);

  // ---------- First-load skeleton data ----------
  // Deterministic placeholder widths so the skeleton reads like the real
  // page (varied line lengths) and never reshuffles between renders.
  /** Widths (px) of the Sun…Sat weekday labels across the calendar header. */
  readonly skWeekdayWidths = [26, 30, 34, 30, 26, 22, 24];

  /**
   * One entry per cell of the 5-week compact month grid (Sun–Sat × 5).
   * `day` / `price` are placeholder line widths in px; `om` marks
   * other-month cells (no price), `dim` mirrors a booked day (the real cell
   * renders a struck-through disabled price) and `hint` adds the small
   * min/max-nights line under the price.
   */
  readonly skCalendarCells: Array<{ day: number; price?: number; dim?: boolean; hint?: boolean; om?: boolean }> = [
    { day: 18, om: true },
    { day: 32, price: 42, hint: true }, { day: 8, price: 38 }, { day: 8, price: 44, dim: true },
    { day: 8, price: 40, hint: true }, { day: 8, price: 36 }, { day: 8, price: 46, dim: true },
    { day: 8, price: 40 }, { day: 8, price: 38, hint: true }, { day: 8, price: 44 },
    { day: 16, price: 42, dim: true }, { day: 16, price: 36 }, { day: 16, price: 48, hint: true }, { day: 16, price: 40 },
    { day: 16, price: 38 }, { day: 16, price: 44, dim: true }, { day: 16, price: 40, hint: true },
    { day: 16, price: 46 }, { day: 16, price: 38 }, { day: 16, price: 42, hint: true }, { day: 16, price: 36, dim: true },
    { day: 16, price: 44 }, { day: 16, price: 40 }, { day: 16, price: 46, hint: true }, { day: 16, price: 38 },
    { day: 16, price: 42, dim: true }, { day: 16, price: 44 }, { day: 16, price: 36, hint: true },
    { day: 16, price: 40 }, { day: 16, price: 46 }, { day: 16, price: 42, dim: true },
    { day: 28, om: true }, { day: 8, om: true }, { day: 8, om: true }, { day: 8, om: true },
  ];

  /** The 7 collapsed optimization-section heads for the default (short-term) tab; widths vary via nth-child CSS. */
  readonly skSections = [1, 2, 3, 4, 5, 6, 7];

  open: Record<SectionKey, boolean> = {
    price: false,
    strategy: false,
    channel: false,
    rank: false,
    discount: false,
    minStay: false,
    listing: false,
    mlComps: false,
    mlTarget: false,
    mlListing: false,
  };

  // ---------- Optimization Settings tabs ----------
  // Replaces the old always-visible "Short-term optimizer" / "Longer-term
  // optimizer" group-label rows: only the active tab's cards render (unless
  // searching — see isSearching, which ignores the tabs entirely).
  settingsTab = 'short';
  // tidy-tabs renders labels as-is, so translate here.
  private readonly allSettingsTabs = [
    { value: 'short', label: new TranslationPipe().transform('Short-term') },
    { value: 'longer', label: new TranslationPipe().transform('Long-term') },
  ];
  // Filtered by the Account Settings long-term toggle (see watchRentalModes).
  // When only one tab survives the bar hides itself — a lone tab reads as chrome.
  settingsTabs = this.allSettingsTabs;

  setSettingsTab(tab: string): void {
    this.settingsTab = tab;
    this.cdr.markForCheck();
  }

  /** Drops the Long-term settings tab when the account has long-term rentals off. */
  private watchRentalModes(): void {
    this.rentalModes.modes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(modes => {
        this.settingsTabs = modes.longTerm
          ? this.allSettingsTabs
          : this.allSettingsTabs.filter(t => t.value !== 'longer');
        if (!this.settingsTabs.some(t => t.value === this.settingsTab)) {
          this.settingsTab = this.settingsTabs[0].value;
        }
        this.cdr.markForCheck();
      });
  }

  // ---------- Header search ----------
  // Typing into the "Search optimizations" input runs each query against the
  // per-subsection synonym lists below. While `isSearching` is true the tabbed
  // layout is hidden and `matchedSubsections` are rendered as standalone
  // cards on the page background (see template `@if (isSearching)` branch).
  readonly searchForm: FormGroup = this.fb.group({ search: [''] });

  matchedSubsections: SubsectionId[] = [];
  // View model for the search-results view — one row per matched subsection
  // (excluding 'calendar', which is never shown there), with `groupLabel` set
  // only on the first row of each contiguous group so the short-term /
  // longer-term divide stays visible without repeating the label per card.
  // Precomputed here so the template never inspects the id string itself.
  matchedSubsectionRows: Array<{ id: SubsectionId; groupLabel: string | null }> = [];

  // Per-subsection synonym dictionary. Match is a normalized substring check,
  // so partial queries ("pr", "rate", "min") progressively narrow.
  // Order here = display order in the search-results view.
  private readonly SUBSECTIONS: Array<{ id: SubsectionId; terms: string[] }> = [
    { id: 'calendar', terms: ['calendar', 'rate', 'rates', 'nightly', 'nightly rate', 'cost', 'date', 'day', 'price', 'pricing', 'daily price'] },
    { id: 'strategy', terms: ['strategy', 'stay length', 'stay type', 'length of stay', 'nights', 'night', 'min nights', 'max nights', 'minimum nights', 'maximum nights', 'short', 'short-term', 'mid', 'mid-term', 'long', 'long-term', 'custom', 'duration', 'stay strategy', 'booking advance', 'advance', 'how far ahead', 'how far in advance', 'availability', 'window', 'months', 'date window', 'date override', 'windows', 'season', 'off-season', 'peak'] },
    { id: 'price-range', terms: ['price', 'pricing', 'price range', 'min', 'max', 'min price', 'max price', 'floor', 'ceiling', 'minimum price', 'maximum price', '$', 'security deposit', 'deposit', 'damage', 'damages', 'direct booking'] },
    { id: 'channel-instant-book', terms: ['instant book', 'instant booking', 'instant', 'track record'] },
    { id: 'channel-non-refundable', terms: ['non-refundable', 'non refundable', 'non-refundable rate', 'refund', 'no refund'] },
    { id: 'channel-seasonal-cancellation', terms: ['seasonal cancellation', 'cancellation', 'vrbo', 'peak'] },
    { id: 'rank-tracker', terms: ['rank', 'ranking', 'rank-based', 'rank based', 'rank tracker', 'position', 'seo', 'visibility', 'tracker', 'search rank'] },
    { id: 'discount-weekly', terms: ['weekly', 'weekly stays', 'weekly discount', 'discount'] },
    { id: 'discount-monthly', terms: ['monthly', 'monthly stays', 'monthly discount', 'discount'] },
    { id: 'discount-last-minute', terms: ['last-minute', 'last minute', 'last minute booking', 'discount'] },
    { id: 'discount-far-out', terms: ['far-out', 'far out', 'early bird', 'early-bird', 'far out booking', 'discount', 'far in advance'] },
    { id: 'discount-member-only', terms: ['member', 'member-only', 'member only', 'member-only exclusive', 'exclusive', 'discount'] },
    { id: 'discount-mobile', terms: ['mobile', 'mobile-only', 'mobile only', 'mobile rate', 'discount'] },
    { id: 'discount-flash', terms: ['flash', 'campaign', 'flash deal', 'flash campaign', 'discount'] },
    { id: 'minstay-checkin-checkout', terms: ['check-in', 'check in', 'checkin', 'check-out', 'check out', 'checkout', 'check-in after', 'check-out by', 'arrival', 'departure'] },
    { id: 'minstay-scheduling', terms: ['restricted days', 'blocked days', 'blackout', 'weekday', 'weekdays', 'restricted check-in', 'restricted check-out', 'advance notice', 'preparation time', 'prep time', 'lead time', 'booking rules', 'misc'] },
    { id: 'listing-content', terms: ['title', 'description', 'amenities', 'amenity', 'photos', 'photo', 'pictures', 'listing builder', 'listing content', 'listing optimization'] },
    { id: 'listing-pet-friendly', terms: ['pet', 'pets', 'pet-friendly', 'pet friendly', 'pet fee', 'animal', 'dog', 'cat'] },
    { id: 'listing-cancellation-policy', terms: ['cancellation', 'cancellation policy', 'cancel', 'refund', 'flexible', 'moderate', 'firm', 'strict', 'policy'] },
    // ---- Longer-term optimizer group ----
    { id: 'ml-comps', terms: ['comps', 'comparable', 'comparables', 'rent comps', 'market rent', 'mid-term', 'mid term', 'mtr', 'long-term', 'long term', 'ltr', 'furnished finder', 'zillow', 'apartments.com', 'apartments com', 'zumper', 'comparable listings'] },
    { id: 'ml-target-rent', terms: ['target rent', 'target', 'floor', 'recommended rent', 'mid-term', 'long-term'] },
    { id: 'ml-listing-optimization', terms: ['completeness', 'listed', 'not listed', 'paused', 'furnished finder', 'zillow', 'apartments.com', 'apartments com', 'zumper', 'listing optimization', 'mid-term', 'long-term'] },
  ];

  // `appliedQuery` is the debounced search string — only updated when the
  // debounced subscription fires. The template drives `isSearching` from
  // this so the page doesn't flip into "search mode" on the first keystroke
  // (which would briefly show an empty "No results" before the matched
  // subsections have been computed).
  private appliedQuery = '';

  get isSearching(): boolean {
    return this.appliedQuery.length > 0;
  }

  // The calendar is permanently rendered at the top of the page, so we
  // exclude it from the empty-state check below the search input — its
  // presence in matchedSubsections shouldn't make the "no matches" banner
  // disappear when no *other* subsections match.
  get matchedSubsectionsExcludingCalendar(): SubsectionId[] {
    return this.matchedSubsections.filter(id => id !== 'calendar');
  }

  clearSearch(): void {
    this.searchForm.get('search')?.setValue('', { emitEvent: false });
    this.applySearch('');
  }

  private applySearch(raw: string): void {
    const query = this.norm(raw);
    this.appliedQuery = query;
    this.matchedSubsections = query
      ? this.SUBSECTIONS.filter(s => s.terms.some(t => this.norm(t).includes(query))).map(s => s.id)
      : [];
    this.matchedSubsectionRows = this.buildMatchedSubsectionRows(this.matchedSubsections);
    this.cdr.markForCheck();
  }

  // Search ignores the tabs and shows matches from both groups, so each row
  // needs its own group label — but only on the first row of a contiguous
  // run, mirroring how the non-search view shows one label per group.
  private buildMatchedSubsectionRows(ids: SubsectionId[]): Array<{ id: SubsectionId; groupLabel: string | null }> {
    let lastGroup: 'short' | 'longer' | null = null;
    const rows: Array<{ id: SubsectionId; groupLabel: string | null }> = [];
    for (const id of ids) {
      if (id === 'calendar') {continue;}
      const group: 'short' | 'longer' = id.startsWith('ml-') ? 'longer' : 'short';
      rows.push({
        id,
        groupLabel: group !== lastGroup ? (group === 'longer' ? 'Long-term' : 'Short-term') : null,
      });
      lastGroup = group;
    }
    return rows;
  }

  private norm(s: string): string {
    return (s ?? '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // ---------- Property picker (drives the Price Optimization calendar) ----------
  // Demo: the hardcoded MOCK_ADDRESSES. Real: replaced in ngOnInit with the
  // customer's real addresses (mapped to the MockAddress shape the calendar
  // only reads `.id` from).
  addresses: MockAddress[] = MOCK_ADDRESSES;
  selectedAddress: MockAddress = MOCK_ADDRESSES[0];
  selectedAddressCityState = [MOCK_ADDRESSES[0].city, MOCK_ADDRESSES[0].state].filter(Boolean).join(', ');
  addressSelectItems: SelectModel[] = MOCK_ADDRESSES.map(a => ({
    value: a.id.toString(),
    viewValue: a.address_name,
  }));
  readonly addressForm: FormGroup = this.fb.group({
    addressValue: [MOCK_ADDRESSES[0].id.toString()],
  });

  onAddressChange(value: string): void {
    const id = parseInt(value, 10);
    const match = this.addresses.find(a => a.id === id);
    if (match) {
      this.selectedAddress = match;
      this.selectedAddressCityState = [match.city, match.state].filter(Boolean).join(', ');
      // Persist the selection so a page refresh restores it instead of falling
      // back to the first property (Trello 18345).
      this.urlState.updateQueryParams({ propertyId: id });
      this.cdr.markForCheck();
      void this.loadMidLongPipeline();
    }
    // Real accounts: switch which address's settings are loaded/persisted.
    if (!this.isDemo && match) {
      // Unsaved edits belong to the previous address; switching discards them
      // (the Save bar was showing) and the new address loads its own baseline.
      this.isDirty.set(false);
      this.realAddressId = id;
      void this.loadForAddress(id);
    }
  }

  /** The property to open on load — the `?propertyId=` from the URL if it maps
   * to a known address, else the first one (Trello 18345). */
  private restoreSelectedAddress(): MockAddress {
    const raw = this.urlState.getQueryParam('propertyId');
    const id = raw ? Number(raw) : NaN;
    return this.addresses.find(a => a.id === id) ?? this.addresses[0];
  }

  // ---------- Price Optimization range toggle (drives the embedded calendar) ----------
  priceMonthRangeLabel = '';

  prevPriceMonth(): void {
    this.optimizerSection?.prevMonth();
    this.refreshPriceMonthLabel();
  }

  nextPriceMonth(): void {
    this.optimizerSection?.nextMonth();
    this.refreshPriceMonthLabel();
  }

  private refreshPriceMonthLabel(): void {
    setTimeout(() => {
      this.priceMonthRangeLabel = this.optimizerSection?.monthRangeLabel ?? defaultMonthRange();
      this.cdr.markForCheck();
    });
  }

  // ---------- Optimizer-active hero (mirrors the Optimizer page) ----------
  readonly daysOptimized = 27;
  private readonly clockSignal = signal(Date.now());
  private readonly clockInterval = setInterval(() => this.clockSignal.set(Date.now()), 30_000);
  private readonly lastOptimizedAtMs = computeLastSixAmPacificMs();
  readonly lastOptimizedLabel = computed(() => formatRelativeAgo(this.clockSignal() - this.lastOptimizedAtMs));

  // ---------- Backend persistence (real accounts only) ----------
  // Demo accounts keep the mock, local-only behavior (no backend calls).
  isDemo = false;
  private hydrating = false;
  private realAddressId: number | null = null;

  /** True while a save PUT is in flight — drives the nav "saving…" spinner. */
  readonly isSaving = signal<boolean>(false);
  /** True for a real account that has no properties yet (prompt to add one). */
  readonly noRealAddress = signal<boolean>(false);
  /** Channels the address can push to (from its listings); null until loaded. */
  readonly connectedPlatforms = signal<Platform[] | null>(null);
  // True once the user edits any setting and hasn't yet saved or discarded.
  // Drives the floating Save / Remove Changes bar above the account-manager pill.
  readonly isDirty = signal<boolean>(false);
  // Note shown in the Discount Optimization section while the backend is still
  // pushing levers out to the channels. Refreshed on load only — reconciliation
  // is asynchronous, so it settles by the next visit rather than live.
  readonly discountSyncNote = signal<string>('');
  // No saved base price: a night with no override and no suggested price then
  // has no price at all, so nothing reaches the channels for it. Tracks the
  // saved value, not the field, so the prompt stays until the base is saved.
  readonly missingBasePrice = signal<boolean>(false);
  // Per-setting channel-sync status, keyed by setting key, for the per-field status dots.
  // Refreshed on load only (reconciliation is asynchronous — see discountSyncNote).
  private syncByKey: Record<string, Partial<Record<string, PlatformSyncStatus>>> = {};
  // The listing builder's rows (title, photos...) live in the listing category; their dots read this map.
  private listingSyncByKey: Record<string, Partial<Record<string, PlatformSyncStatus>>> = {};
  /** The listing builder's basic fields, shown read-only on the Listing Optimization card. */
  readonly listingContent = signal<ListingContentSummary | null>(null);
  readonly listingContentRows = computed(() => {
    const content = this.listingContent();
    if (!content) { return []; }
    const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
    return [
      { key: 'title', label: 'Title', value: content.title || 'Not set' },
      { key: 'description', label: 'Description', value: content.description || 'Not set' },
      { key: 'amenities', label: 'Amenities', value: count(content.amenityCount, 'amenity', 'amenities') },
      { key: 'photos', label: 'Photos', value: count(content.photoCount, 'photo', 'photos') },
    ];
  });
  // Dot for the Date Overrides field: the per-date effective min-stay status
  // (`min_stay` on the by-date store), aggregated over the override window span.
  dateOverridesSync: SyncDotInfo | null = null;
  // Per-control validation messages from the last rejected save, keyed by
  // `bookingForm` control name. Read straight by the template (not a function
  // call) and paired with `submitted`, which is what makes tidy-input show them.
  fieldErrors: Record<string, string> = {};
  submitted = false;
  // Whether this page is the active view. Ionic keeps inactive pages mounted, so
  // we gate the app-root save bar on this to hide it the moment we navigate away.
  private readonly isActive = signal<boolean>(true);
  // The last loaded-or-saved settings for the current address. "Remove Changes"
  // re-applies this; "Save Changes" replaces it with the just-saved values.
  private baselineModel: OptimizerSettings | null = null;
  // Last price range persisted to the optimizer-category rows (dollars), so
  // unrelated edits don't re-PUT the bounds and re-trigger a pricing sync.
  // `securityDeposit` rides along but saves on its own path (see saveSnapshot).
  private lastSavedPrice: { addressId: number; basePrice: number | null; minPrice: number | null; maxPrice: number | null; securityDeposit: number | null } | null = null;
  // Seed fed to the Rental Types child's `value` input. It changes only on
  // address load, so the child re-applies on hydrate but NOT in response to the
  // user's own edits (which would rebuild its forms and steal focus).
  // `rentalTypesState` holds the latest edited value, read at save time.
  rentalTypesSeed: RentalTypesState | null = null;
  rentalTypesState: RentalTypesState | null = null;

  ngOnInit(): void {
    if (this.embedded()) {
      this.initEmbedded(this.embeddedAddressId()!);
      return;
    }
    this.watchRentalModes();
    this.isDemo = this.demoBlock.isDemo;
    if (this.isDemo) {
      // Demo picker is local-only, but still honor a saved property so a
      // refresh keeps the user's selection (Trello 18345).
      this.selectedAddress = this.restoreSelectedAddress();
      this.selectedAddressCityState = [this.selectedAddress.city, this.selectedAddress.state].filter(Boolean).join(', ');
      this.addressForm.get('addressValue')!.setValue(this.selectedAddress.id.toString(), { emitEvent: false });
    }
    this.refreshPriceMonthLabel();
    // Long debounce so the "No results" empty state doesn't flash while the
    // user is still mid-word ("p", "pr", "pri" all bounce until "price").
    this.searchForm.get('search')!.valueChanges
      .pipe(debounceTime(700), takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this.applySearch(v ?? ''));
    // Longer-term optimizer pipeline — comps, targets, and channel rows all
    // key off the selected address. Real accounts run it from
    // initRealAddresses instead: `selectedAddress` is still the mock
    // placeholder here, so running it now would seed the form and its baseline
    // from a demo address's localStorage key.
    if (this.isDemo) {
      void this.loadMidLongPipeline();
    }
    // No backend for longer-term targets yet — they ride the page's normal
    // dirty → "Save Changes" flow and land in localStorage.
    this.targetForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.markDirty());

    // Real accounts: load real addresses + persist settings per address. Demo
    // accounts skip all of this and keep today's mock, local-only behavior.
    if (!this.isDemo) {
      // Start blank immediately (before change-tracking is wired) so a real
      // account never flashes the mock initializer values before its settings load.
      this.applyModel(defaultSettings());
      this.wireDirtyTracking();
      void this.initRealAddresses();
      // The Save / Remove bar lives at the app root — hand it our save + discard.
      this.saveBar.claim(this.saveBarOwner);
    }

    // The page is mock-data-backed (nothing async to await), so hold the
    // skeleton just long enough for its staggered entrance to finish, then
    // swap the real content in place.
    setTimeout(() => this.isLoaded.set(true), 900);
  }

  ngOnDestroy(): void {
    clearInterval(this.clockInterval);
    this.saveBar.release(this.saveBarOwner);
  }

  private initEmbedded(addressId: number): void {
    this.isDemo = this.demoBlock.isDemo;
    this.isLoaded.set(true);
    if (this.isDemo) { return; }
    this.applyModel(defaultSettings());
    this.wireDirtyTracking();
    this.realAddressId = addressId;
    void this.loadForAddress(addressId);
  }

  /**
   * Re-reads the property when nothing is unsaved. Ionic keeps both this page and the
   * listing builder mounted, and both save the same rows, so a page showing what it
   * loaded earlier would write the other page's newer values back on its next save.
   */
  reloadIfClean(): void {
    if (this.isDemo || this.realAddressId === null || this.isDirty() || this.isSaving()) { return; }
    void this.loadForAddress(this.realAddressId);
  }

  /**
   * The listing builder's Save runs this after its own write. Resolves true when
   * there was nothing to save or every write landed; failures already showed their error.
   */
  async saveFromHost(): Promise<boolean> {
    if (!this.isDirty()) { return true; }
    await this.saveChanges();
    return !this.isDirty();
  }

  // Ionic keeps this page mounted when navigating forward, so toggle the
  // app-root save bar on view enter/leave rather than on init/destroy alone.
  ionViewWillEnter(): void {
    this.isActive.set(true);
    if (!this.embedded()) {
      this.reloadIfClean();
      if (!this.isDemo) { this.saveBar.claim(this.saveBarOwner); }
    }
    // Mobile returns from the pushed day-detail page here rather than through
    // a right-side-panel close, so refresh the grid for any write it made.
    this.optimizerSection?.reloadIfDayPanelWasOpen();
  }

  ionViewWillLeave(): void {
    this.isActive.set(false);
  }

  // ---------- Persistence: load / save (real accounts only) ----------

  private wireDirtyTracking(): void {
    // Every input/select lives in the reactive form, so form value changes cover
    // all of them; the boolean/enum/record props and the Rental Types child call
    // markDirty() directly. Guarded so hydrating a freshly-loaded address (which
    // patches with emitEvent:false anyway) or demo mode never marks dirty.
    this.bookingForm.valueChanges
      .pipe(
        filter(() => !this.isDemo && !this.hydrating),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.markDirty());
  }

  private async initRealAddresses(): Promise<void> {
    let real: Address[] = [];
    try {
      real = await this.addressesQueries.getAddresses();
    } catch {
      real = [];
    }
    if (!real.length) {
      this.addresses = [];
      this.addressSelectItems = [];
      this.realAddressId = null;
      this.noRealAddress.set(true);
      this.cdr.markForCheck();
      return;
    }
    this.noRealAddress.set(false);
    this.addresses = real.map(a => this.toMockShape(a));
    // Restore the last-selected property from the URL so a refresh keeps it
    // instead of resetting to the first one (Trello 18345).
    this.selectedAddress = this.restoreSelectedAddress();
    this.selectedAddressCityState = [this.selectedAddress.city, this.selectedAddress.state].filter(Boolean).join(', ');
    this.addressSelectItems = this.addresses.map(a => ({ value: a.id.toString(), viewValue: a.address_name }));
    this.addressForm.get('addressValue')!.setValue(this.selectedAddress.id.toString(), { emitEvent: false });
    this.realAddressId = this.selectedAddress.id;
    this.cdr.markForCheck();
    // ngOnInit ran this against the placeholder mock address — redo it now
    // that the real property is selected.
    void this.loadMidLongPipeline();
    await this.loadForAddress(this.realAddressId);
  }

  private toMockShape(a: Address): MockAddress {
    const name = a.address_name || a.address1 || `Property ${a.id}`;
    return { id: a.id, address_name: name, display_name: name, city: a.city, state: a.state, color: a.color };
  }

  // The aggregate sync status for a field plus the platforms that failed (for the
  // tooltip), over every backing setting key. A field can cover more than one key
  // (e.g. the check-in/out times, or the two closed-to lists).
  // @param keys the setting key(s) that back the field
  syncDot(...keys: string[]): SyncDotInfo {
    return this.dotFor(keys, keys.map(key => this.syncByKey[key]));
  }

  /** The same dot for a listing builder field (title, photos...). */
  listingSyncDot(...keys: string[]): SyncDotInfo {
    return this.dotFor(keys, keys.map(key => this.listingSyncByKey[key]));
  }

  private dotFor(keys: string[], maps: Array<Partial<Record<string, PlatformSyncStatus>> | undefined>): SyncDotInfo {
    return {
      status: aggregateSyncStatus(maps),
      syncedPlatforms: syncedSyncPlatforms(maps).map(formatPlatformName),
      pendingPlatforms: pendingSyncPlatforms(maps).map(formatPlatformName),
      failedPlatforms: failedSyncPlatforms(maps).map(formatPlatformName),
      unsupportedPlatforms: unsupportedSyncPlatforms(maps).map(formatPlatformName),
      supportedPlatforms: coveragePlatforms(keys).map(formatPlatformName),
    };
  }

  // The price range (base/min/max) syncs to TIDY's pricing API, which prices the
  // nights sent to every connected channel — so the `tidy_pricing` status gets its
  // own name rather than any one channel's. `pricelabs` is what the back end
  // reported before gotidy #8949 (PriceLabs set the Airbnb price); kept so this
  // can ship before or after it. Drop it once #8949 is live.
  priceSyncDot(): SyncDotInfo {
    const maps = ['base_price', 'min_price', 'max_price'].map(key => this.syncByKey[key]);
    const names: Record<string, string> = { tidy_pricing: 'TIDY pricing', pricelabs: 'Airbnb' };
    const label = (slug: string): string => names[slug] ?? formatPlatformName(slug);
    return {
      status: aggregateSyncStatus(maps),
      syncedPlatforms: syncedSyncPlatforms(maps).map(label),
      pendingPlatforms: pendingSyncPlatforms(maps).map(label),
      failedPlatforms: failedSyncPlatforms(maps).map(label),
      unsupportedPlatforms: unsupportedSyncPlatforms(maps).map(label),
    };
  }

  /** GET the address's listing settings + optimizer price defaults and hydrate the whole page (no autosave). */
  private async loadForAddress(id: number): Promise<void> {
    this.hydrating = true;
    const [settingsResult, defaultsResult] = await Promise.allSettled([
      this.listingSettings.getAddressWithSettings(id),
      this.listingSettings.getOptimizerDefaults(id),
    ]);
    const settingRows = settingsResult.status === 'fulfilled'
      ? settingsResult.value.address_settings
      : undefined;
    const model: OptimizerSettings = settingRows
      ? fromSettings(settingRows)
      : defaultSettings();
    this.discountSyncNote.set(buildDiscountSyncNote(settingRows));
    // The price range is authoritative in the optimizer-category rows (synced
    // to TIDY pricing; also edited from the day-detail panel) — never the blob.
    model.price = defaultsResult.status === 'fulfilled'
      ? {
          basePrice: defaultsResult.value.basePrice === null ? null : defaultsResult.value.basePrice / 100,
          minPrice: defaultsResult.value.minPrice === null ? null : defaultsResult.value.minPrice / 100,
          maxPrice: defaultsResult.value.maxPrice === null ? null : defaultsResult.value.maxPrice / 100,
          securityDeposit: defaultsResult.value.securityDeposit === null ? null : defaultsResult.value.securityDeposit / 100,
        }
      : { basePrice: null, minPrice: null, maxPrice: null, securityDeposit: null };
    // The user may have switched properties while these GETs were in flight —
    // applying now would hand this address's settings (and baseline) to the
    // other one, and the next Save would PUT them under its id. The newer
    // load owns `hydrating` from here, so leave it alone on the way out.
    if (this.realAddressId !== id) { return; }
    this.rebuildSyncByKey(settingRows);
    this.listingContent.set(settingRows ? listingContentSummary(settingRows) : null);
    this.lastSavedPrice = { addressId: id, ...model.price };
    this.missingBasePrice.set(defaultsResult.status === 'fulfilled' && model.price.basePrice === null);
    this.applyModel(model);
    this.baselineModel = this.snapshot(model);
    this.isDirty.set(false);
    this.hydrating = false;
    this.cdr.markForCheck();
    void this.loadConnectedPlatforms(id);
    void this.loadDateOverrideSync(id, model.rentalTypes.windows);
  }

  private rebuildSyncByKey(settingRows: AddressSetting[] | undefined): void {
    this.syncByKey = {};
    this.listingSyncByKey = {};
    for (const setting of settingRows ?? []) {
      if (setting.category === OPTIMIZER_SETTING_CATEGORY) {
        this.syncByKey[setting.key] = setting.platform_sync ?? {};
      } else if (setting.category === LISTING_SETTING_CATEGORY) {
        this.listingSyncByKey[setting.key] = setting.platform_sync ?? {};
      }
    }
  }

  // Re-read the settings after a save so the dots reflect the new state — a changed
  // row goes back to `pending` until the channels re-confirm it. Refreshes only the
  // dot data, not the form, so an edit made while the save was in flight isn't lost.
  private async refreshSyncStatus(addressId: number): Promise<void> {
    try {
      const address = await this.listingSettings.getAddressWithSettings(addressId);
      if (this.realAddressId !== addressId) { return; }
      this.rebuildSyncByKey(address.address_settings);
      this.cdr.markForCheck();
    } catch {
      // Keep the last-known dots if the refresh fails.
    }
  }

  // The Date Overrides field syncs per date (effective min-stay), not through an
  // address-setting row — so its dot reads the by-date store, aggregated over the
  // span the override windows cover. No windows → nothing to sync → no dot.
  private async loadDateOverrideSync(
    addressId: number,
    windows: RentalTypesState['windows'],
  ): Promise<void> {
    this.dateOverridesSync = null;
    const starts = windows.map(w => w.start).filter(Boolean).sort();
    const ends = windows.map(w => w.end).filter(Boolean).sort();
    if (!starts.length || !ends.length) { this.cdr.markForCheck(); return; }
    try {
      const rows = await this.listingCalendar.getDateSettings(addressId, starts[0], ends[ends.length - 1]);
      if (this.realAddressId !== addressId) { return; }
      const maps = rows.map(row => row.platformSync['min_stay']).filter(Boolean);
      this.dateOverridesSync = {
        status: aggregateSyncStatus(maps),
        syncedPlatforms: syncedSyncPlatforms(maps).map(formatPlatformName),
        failedPlatforms: failedSyncPlatforms(maps).map(formatPlatformName),
      };
    } catch {
      this.dateOverridesSync = null;
    }
    this.cdr.markForCheck();
  }

  // Listing optimization moved to a TIDY-run recurring process surfaced on the
  // Listings page, so this page no longer shows run history — but the "no
  // listing" save warning still needs to know which channels the address can
  // push to, which its listings answer.
  private async loadConnectedPlatforms(addressId: number): Promise<void> {
    try {
      const listings = await this.client.getAddressListings(addressId);
      this.connectedPlatforms.set(connectedPlatformsFrom(listings || []));
    } catch {
      this.connectedPlatforms.set(null);
    }
  }

  // Push a loaded model into the form + props + child WITHOUT triggering autosave
  // (form patched with emitEvent:false; `hydrating` guards the Subject path).
  private applyModel(m: OptimizerSettings): void {
    this.bookingForm.patchValue({
      basePrice: m.price.basePrice,
      minPrice: m.price.minPrice,
      maxPrice: m.price.maxPrice,
      securityDeposit: m.price.securityDeposit,
      discountWeekly: m.discounts.weekly.pct,
      discountMonthly: m.discounts.monthly.pct,
      discountLastMinute: m.discounts.lastMinute.pct,
      discountLastMinuteDays: m.discounts.lastMinute.days,
      discountFarOut: m.discounts.farOut.pct,
      discountFarOutDays: m.discounts.farOut.days,
      discountMemberOnly: m.discounts.memberOnly.pct,
      discountMobile: m.discounts.mobile.pct,
      discountFlash: m.discounts.flash.pct,
      advanceNotice: m.minStay.advanceNotice,
      prepTime: m.minStay.prepTime,
      checkInAfter: m.minStay.checkInAfter,
      checkOutBy: m.minStay.checkOutBy,
      petFee: m.listing.petFee,
    }, { emitEvent: false });

    this.instantBook = m.channel.instantBook;
    this.instantTrackRecord = m.channel.instantTrackRecord;
    this.nonRefundableRate = m.channel.nonRefundableRate;
    // Seed (new ref) re-hydrates the child; state is the latest for saving.
    this.seasonalCancellationSeed = m.channel.seasonalCancellation;
    this.seasonalCancellationState = m.channel.seasonalCancellation;

    this.discountWeeklyOn = m.discounts.weekly.on;
    this.discountMonthlyOn = m.discounts.monthly.on;
    this.discountLastMinuteOn = m.discounts.lastMinute.on;
    this.discountFarOutOn = m.discounts.farOut.on;
    this.discountMemberOnlyOn = m.discounts.memberOnly.on;
    this.discountMobileOn = m.discounts.mobile.on;
    this.discountFlashOn = m.discounts.flash.on;

    this.petFriendly = m.listing.petFriendly;
    this.cancellationPolicy = m.listing.cancellationPolicy;
    this.restrictedDays = { ...m.minStay.restrictedDays };
    // Seed (new ref) re-hydrates the child; state is the latest for saving.
    this.rentalTypesSeed = m.rentalTypes;
    this.rentalTypesState = m.rentalTypes;
  }

  private collectModel(): OptimizerSettings {
    const f = this.bookingForm.getRawValue();
    return {
      price: { basePrice: numOrNull(f.basePrice), minPrice: numOrNull(f.minPrice), maxPrice: numOrNull(f.maxPrice), securityDeposit: numOrNull(f.securityDeposit) },
      channel: {
        instantBook: this.instantBook,
        instantTrackRecord: this.instantTrackRecord,
        nonRefundableRate: this.nonRefundableRate,
        seasonalCancellation: this.seasonalCancellationState,
      },
      discounts: {
        weekly: { on: this.discountWeeklyOn, pct: numOrNull(f.discountWeekly) },
        monthly: { on: this.discountMonthlyOn, pct: numOrNull(f.discountMonthly) },
        lastMinute: { on: this.discountLastMinuteOn, pct: numOrNull(f.discountLastMinute), days: numOrNull(f.discountLastMinuteDays) },
        farOut: { on: this.discountFarOutOn, pct: numOrNull(f.discountFarOut), days: numOrNull(f.discountFarOutDays) },
        memberOnly: { on: this.discountMemberOnlyOn, pct: numOrNull(f.discountMemberOnly) },
        mobile: { on: this.discountMobileOn, pct: numOrNull(f.discountMobile) },
        flash: { on: this.discountFlashOn, pct: numOrNull(f.discountFlash) },
      },
      minStay: {
        advanceNotice: numOrNull(f.advanceNotice),
        prepTime: numOrNull(f.prepTime),
        checkInAfter: f.checkInAfter ?? '',
        checkOutBy: f.checkOutBy ?? '',
        restrictedDays: { ...this.restrictedDays },
      },
      listing: {
        petFriendly: this.petFriendly,
        petFee: numOrNull(f.petFee),
        cancellationPolicy: this.cancellationPolicy,
      },
      rentalTypes: this.rentalTypesState ?? defaultSettings().rentalTypes,
    };
  }

  /** Persist the full model; resolves true only if every write succeeded. */
  private async saveSnapshot(addressId: number, model: OptimizerSettings): Promise<boolean> {
    if (this.isDemo) { return false; }
    // Every lever goes up in one batch and the backend rejects the batch as a
    // unit, so one out-of-range percent would silently lose every other edit on
    // the page. Catch it here, mark each offending field, and keep the form as
    // the customer left it so they only have to fix what's flagged.
    const invalid = [
      ...validateOptimizerSettings(model),
      ...validatePriceBounds(model.price, this.storedPrice()),
      ...validateStayLength(model.rentalTypes),
      ...validateSeasonalCancellation(model.channel.seasonalCancellation, this.baselineModel?.channel.seasonalCancellation ?? []),
    ];
    if (invalid.length) {
      this.applyFieldErrors(invalid);
      return false;
    }
    this.clearFieldErrors();
    this.isSaving.set(true);
    const saveListing = async (): Promise<boolean> => {
      try {
        const entries = this.embedded()
          ? withoutBuilderOwnedKeys(toEntries(model))
          : toEntries(model);
        await this.listingSettings.upsertListingSettings(addressId, entries);
        return true;
      } catch (error) {
        // Prefer whatever the API said — a rejected save has a real reason, and
        // blaming the network sends the customer to check their wifi instead.
        this.util.showError(this.saveErrorMessage(error, 'Couldn’t save your changes. Please try again.'));
        return false;
      }
    };
    // The price range writes to its own backend-validated endpoint (pushed to
    // TIDY pricing), so only send it when it actually changed — every other edit
    // must not re-trigger a pricing sync.
    const savePrice = async (): Promise<boolean> => {
      if (!this.priceChanged(addressId, model)) { return true; }
      try {
        await this.listingSettings.upsertOptimizerDefaults(addressId, {
          basePrice: model.price.basePrice === null ? null : Math.round(model.price.basePrice) * 100,
          minPrice: model.price.minPrice === null ? null : Math.round(model.price.minPrice) * 100,
          maxPrice: model.price.maxPrice === null ? null : Math.round(model.price.maxPrice) * 100,
        });
        // Record only this saver's slice — the deposit may still be unsaved.
        this.lastSavedPrice = {
          ...(this.lastSavedPrice ?? { addressId, ...model.price }),
          addressId,
          basePrice: model.price.basePrice,
          minPrice: model.price.minPrice,
          maxPrice: model.price.maxPrice,
        };
        // A save can add a base but never clear one, so it only ever turns the prompt off.
        if (model.price.basePrice !== null && this.realAddressId === addressId) { this.missingBasePrice.set(false); }
        return true;
      } catch (error) {
        this.util.showError(this.saveErrorMessage(
          error,
          'Couldn’t save your price range. Base, min, and max must be whole dollar amounts with min ≤ max.'
        ));
        return false;
      }
    };
    // The direct-booking security deposit shares the optimizer-defaults rows
    // but not the price path: the backend rejects a null upsert, so clearing
    // the field DELETEs the row instead. Whole dollars > 0, sent as cents.
    const saveDeposit = async (): Promise<boolean> => {
      if (!this.depositChanged(addressId, model)) { return true; }
      const deposit = model.price.securityDeposit;
      if (deposit !== null && (!Number.isInteger(deposit) || deposit <= 0)) {
        this.util.showError('Security deposit must be a whole dollar amount greater than zero.');
        return false;
      }
      try {
        if (deposit === null) {
          await this.listingSettings.deleteOptimizerDefaults(addressId, ['security_deposit']);
        } else {
          await this.listingSettings.upsertOptimizerDefaults(addressId, { securityDeposit: deposit * 100 });
        }
        // Record only this saver's slice — the price range may still be unsaved.
        this.lastSavedPrice = {
          ...(this.lastSavedPrice ?? { addressId, ...model.price }),
          addressId,
          securityDeposit: deposit,
        };
        return true;
      } catch {
        this.util.showError('Couldn’t save your security deposit. It must be a whole dollar amount greater than zero.');
        return false;
      }
    };
    try {
      const [listingOk, priceOk, depositOk] = await Promise.all([saveListing(), savePrice(), saveDeposit()]);
      return listingOk && priceOk && depositOk;
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * The API's own explanation of a rejected write, falling back to `fallback`
   * for a genuine transport failure (offline, timeout, 5xx with no body).
   * Without this a 400 reads as "check your connection", which sends the
   * customer to debug their wifi while the real reason sits in the response.
   */
  private saveErrorMessage(error: unknown, fallback: string): string {
    // A bodiless failure (offline, timeout, bare 5xx) reaches us as the raw
    // HttpErrorResponse, whose `.message` is the technical "Http failure
    // response for <url>…" string — the fallback is the right thing to show.
    if (error instanceof HttpErrorResponse) { return fallback; }
    // Everything else has been through ErrorInterceptor.formatError, which
    // unwraps an API error to `{ code, message, err }` — so the text sits at
    // the TOP level, not under `.error` the way a raw response would.
    const e = error as { message?: unknown; error?: { message?: unknown }; err?: { error?: { message?: unknown } } };
    const candidates = [
      typeof error === 'string' ? error : undefined,
      e?.message,
      e?.err?.error?.message,
      e?.error?.message,
    ];
    const message = candidates.find(c => typeof c === 'string' && c.trim());
    return typeof message === 'string' ? message : fallback;
  }

  /** The price as last loaded or saved — what a cleared field would be removing. */
  private storedPrice(): OptimizerSettings['price'] | null {
    const last = this.lastSavedPrice;
    if (!last || last.addressId !== this.realAddressId) { return null; }
    return {
      basePrice: last.basePrice,
      minPrice: last.minPrice,
      maxPrice: last.maxPrice,
      securityDeposit: last.securityDeposit,
    };
  }

  /**
   * Marks every offending control so the fields carry their own message, rather
   * than a single toast the customer has to match to a field by hand. The
   * control is forced invalid because tidy-input renders its error on
   * `!form.valid && submitted`.
   */
  private applyFieldErrors(errors: OptimizerValidationError[]): void {
    this.clearFieldErrors();
    const messages: Record<string, string> = {};
    for (const { field, message } of errors) {
      // First message per field wins — later ones are usually consequences.
      if (messages[field]) { continue; }
      messages[field] = message;
      this.bookingForm.get(field)?.setErrors({ optimizer: true });
    }
    this.fieldErrors = messages;
    this.submitted = true;
    // The message points at a highlighted field, so the section holding it can't stay
    // collapsed — expand the one that owns the first problem.
    const section = this.sectionForField(errors[0].field);
    if (section) {
      this.open[section] = true;
      // Bring it on screen once the body has rendered.
      setTimeout(() => {
        this.host.nativeElement.querySelector(`[data-section="${section}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    // Lead with the first problem; the rest are marked on their own fields.
    this.util.showError(errors[0].message);
    this.cdr.markForCheck();
  }

  /** The collapsible section a validation error's field lives in. */
  private sectionForField(field: string): SectionKey | null {
    if (field.startsWith('discount')) { return 'discount'; }
    if (field === 'basePrice' || field === 'minPrice' || field === 'maxPrice') { return 'price'; }
    if (field === 'minNights' || field === 'windows') { return 'strategy'; }
    if (field === 'seasonalWindows') { return 'channel'; }
    return null;
  }

  /** Drops the marks once the customer starts fixing things. */
  private clearFieldErrors(): void {
    for (const field of Object.keys(this.fieldErrors)) {
      const control = this.bookingForm.get(field);
      if (control?.hasError('optimizer')) { control.setErrors(null); }
    }
    this.fieldErrors = {};
    this.submitted = false;
  }

  private priceChanged(addressId: number, model: OptimizerSettings): boolean {
    const last = this.lastSavedPrice;
    return !last
      || last.addressId !== addressId
      || last.basePrice !== model.price.basePrice
      || last.minPrice !== model.price.minPrice
      || last.maxPrice !== model.price.maxPrice;
  }

  private depositChanged(addressId: number, model: OptimizerSettings): boolean {
    const last = this.lastSavedPrice;
    return !last
      || last.addressId !== addressId
      || last.securityDeposit !== model.price.securityDeposit;
  }

  /** Called by every non-form mutation (toggles, chips, weekday, Rental Types). */
  markDirty(): void {
    if (this.isDemo || this.hydrating) { return; }
    // Compare, don't just flag: undoing an edit by hand (Short → back to Custom)
    // should drop the bar the same way "Remove Changes" does.
    this.isDirty.set(this.differsFromBaseline());
    // Any edit is an attempt to fix the flagged field, so stop marking it —
    // the next save re-runs validation and re-marks whatever is still wrong.
    if (this.submitted) { this.clearFieldErrors(); }
  }

  /** Whether the page differs from what was loaded or last saved (same compare the save uses). */
  private differsFromBaseline(): boolean {
    if (!this.baselineModel) { return true; }
    return JSON.stringify(this.collectModel()) !== JSON.stringify(this.baselineModel)
      || JSON.stringify(this.collectLongerTermTargets()) !== JSON.stringify(this.longerTermBaseline);
  }

  onRentalTypesChange(state: RentalTypesState): void {
    if (this.hydrating) { return; }
    this.rentalTypesState = state;
    this.markDirty();
  }

  /**
   * Save bar → green "Save Changes". Short-term settings go to the backend;
   * the long-term tab has no endpoints yet and is local-only, so a long-term
   * edit on its own never issues a request (it used to PUT the unchanged
   * short-term blob and surface that call's failure).
   */
  saveChanges = async (): Promise<void> => {
    if (this.isDemo || !this.isDirty() || this.isSaving()) { return; }
    if (this.realAddressId === null || this.realAddressId === undefined) { return; }
    const addressId = this.realAddressId;
    const model = this.collectModel();
    // Local-only, can't fail — commit it (and its baseline) before the awaited
    // backend writes so a network error can't strand it half-saved.
    const longerTerm = this.collectLongerTermTargets();
    // The embedded copy never loads the long-term targets, so writing its blanks would wipe them.
    if (!this.embedded()) {
      this.writeLongerTermTargets(addressId, longerTerm);
      this.longerTermBaseline = { ...longerTerm };
    }
    const shortTermChanged = !this.baselineModel
      || JSON.stringify(model) !== JSON.stringify(this.baselineModel);
    const ok = shortTermChanged ? await this.saveSnapshot(addressId, model) : true;
    // Ignore a stale save if the user switched addresses mid-flight.
    if (this.realAddressId !== addressId) { return; }
    if (ok) {
      // A setting none of whose channels has a listing is stored but never pushed —
      // say so, or the save reads as done while nothing happens on the channel.
      if (shortTermChanged) {
        const changed = changedSettingKeys(this.baselineModel ? toEntries(this.baselineModel) : [], toEntries(model));
        const warning = noListingWarning(changed, this.connectedPlatforms());
        if (warning) { void this.util.showWarning(warning); }
      }
      this.baselineModel = this.snapshot(model);
      // Re-read the sync state so the dots flip to pending on what we just pushed.
      if (shortTermChanged) {
        void this.refreshSyncStatus(addressId);
        void this.loadDateOverrideSync(addressId, model.rentalTypes.windows);
      }
      // Inputs stay editable during the PUT, so only go clean if nothing changed
      // while it was in flight — otherwise keep the bar up for the newer edits.
      const changedDuringSave =
        JSON.stringify(this.collectModel()) !== JSON.stringify(model) ||
        JSON.stringify(this.collectLongerTermTargets()) !== JSON.stringify(longerTerm);
      this.isDirty.set(changedDuringSave);
      this.cdr.markForCheck();
    }
  };

  /** Save bar → black "Remove Changes". Reverts every field to the last saved values. */
  discardChanges = (): void => {
    if (!this.isDirty()) { return; }
    // Guard so re-applying the baseline (form patch + child re-seed) doesn't
    // itself flip the page back to dirty.
    this.hydrating = true;
    if (this.baselineModel) {
      this.applyModel(this.snapshot(this.baselineModel));
    }
    this.applyLongerTermTargets({ ...this.longerTermBaseline });
    this.hydrating = false;
    this.isDirty.set(false);
    this.cdr.markForCheck();
  };

  /** Deep clone so the baseline can't be mutated by later edits (and re-seeds the child). */
  private snapshot(m: OptimizerSettings): OptimizerSettings {
    return JSON.parse(JSON.stringify(m));
  }

  // No component import: the listing builder imports this page to embed it, so
  // importing it back is a module cycle. navigateTo resolves it from the routes.
  async openListingBuilder(): Promise<void> {
    if (this.realAddressId === null) { return; }
    // Unsaved edits here would skip the re-read on return, and their save would undo the builder's.
    if (this.isDirty()) {
      this.util.showError('Save or remove your changes here first.');
      return;
    }
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Listings');
      await this.rightSidePanelService.navigateTo('property-listings', { addressId: this.realAddressId });
    } else {
      await this.storage.save('dialog-params', { addressId: this.realAddressId });
      this.navCtrl.navigateForward('property-listings');
    }
  }

  goToProperties(): void {
    // The desktop "Properties" nav item routes to /overview.
    this.navCtrl.navigateForward('properties');
  }

  // ---------- Channel optimization (toggles) ----------
  instantBook = true;
  instantTrackRecord = true;
  nonRefundableRate = false;

  // VRBO seasonal windows: null seed keeps the child's demo sample until real
  // settings hydrate; state carries the latest edit for saving.
  seasonalCancellationSeed: SeasonalWindowState[] | null = null;
  seasonalCancellationState: SeasonalWindowState[] = [];

  onSeasonalCancellationChange(state: SeasonalWindowState[]): void {
    if (this.hydrating) { return; }
    this.seasonalCancellationState = state;
    this.markDirty();
  }

  // ---------- Discount toggles ----------
  discountWeeklyOn = true;
  discountMonthlyOn = true;
  discountLastMinuteOn = true;
  discountFarOutOn = false;
  discountMemberOnlyOn = true;
  discountMobileOn = true;
  discountFlashOn = false;

  // ---------- Listing optimization toggles & chip state ----------
  petFriendly = true;
  cancellationPolicy: CancellationPolicy | '' = 'moderate';
  readonly cancellationPolicies: Array<{ value: CancellationPolicy; label: string }> = [
    { value: 'flexible', label: 'Flexible' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'firm',     label: 'Firm' },
    { value: 'strict',   label: 'Strict' },
  ];

  // ---------- Weekday restriction chips ----------
  restrictedDays: Record<string, boolean> = { Sun: false, Mon: false, Tue: false, Wed: false, Thu: false, Fri: false, Sat: false };
  readonly weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  toggleDay(day: string): void {
    if (this.demoBlock.block()) { return; }
    this.restrictedDays[day] = !this.restrictedDays[day];
    this.markDirty();
  }

  // ---------- Reactive form holding every numeric/text input + select ----------
  // tidy-input and tidy-select require a FormControl — we centralize them
  // here so the template can pass `bookingForm.controls.X` everywhere.
  readonly bookingForm: FormGroup = this.fb.group({
    basePrice: [289],
    minPrice: [199],
    maxPrice: [489],
    securityDeposit: [null],
    discountWeekly: [8],
    discountMonthly: [22],
    discountLastMinute: [12],
    discountLastMinuteDays: [5],
    discountFarOut: [6],
    discountFarOutDays: [180],
    discountMemberOnly: [5],
    discountMobile: [4],
    discountFlash: [15],
    // Bounded to what the channels accept — an out-of-range number would otherwise be
    // rejected server-side and fail the whole save, not just this field.
    advanceNotice: [1, [Validators.min(Math.min(...ADVANCE_NOTICE_DAYS)), Validators.max(Math.max(...ADVANCE_NOTICE_DAYS))]],
    prepTime: [1, [Validators.min(Math.min(...PREPARATION_TIME_NIGHTS)), Validators.max(Math.max(...PREPARATION_TIME_NIGHTS))]],
    checkInAfter: ['15:00'],
    checkOutBy: ['11:00'],
    petFee: [50],
  });

  // SelectModel lists used by the time-of-day tidy-selects.
  readonly timeSelectItems: SelectModel[] = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00']
    .map(t => ({ value: t, viewValue: t }));

  // =========================================================================
  // ---------- Longer-term optimizer (separate, shorter pipeline) -----------
  // =========================================================================
  // Demo accounts render the address-keyed fixtures below. Real accounts have
  // no longer-term backend yet, so comps/channels come up empty and the only
  // editable piece (target rent) persists to localStorage through the same
  // dirty → Save Changes flow as the rest of the page.
  private readonly rentalStrategy = inject(RentalStrategyService);

  // ---- Comps ----
  compsTerm: MidLongTerm = 'mid';
  comps: CompListing[] = [];
  compsSummary: CompSummary = { medianCents: 0, lowCents: 0, highCents: 0 };

  setCompsTerm(term: MidLongTerm): void {
    this.compsTerm = term;
    this.refreshComps();
  }

  private refreshComps(): void {
    this.comps = this.isDemo ? getComps(this.selectedAddress.id, this.compsTerm) : [];
    this.compsSummary = this.comps.length
      ? summarizeComps(this.comps)
      : { medianCents: 0, lowCents: 0, highCents: 0 };
    this.cdr.markForCheck();
  }

  // ---- Target rent ----
  // Stored in dollars; the demo service stores CENTS, converted on load.
  readonly targetForm: FormGroup = this.fb.group({
    midTarget: this.fb.control<number | null>(null),
    midFloor: this.fb.control<number | null>(null),
    longTarget: this.fb.control<number | null>(null),
    longFloor: this.fb.control<number | null>(null),
  });

  midRecommendedLabel = '';
  longRecommendedLabel = '';

  // Last saved longer-term values for the current address — "Remove Changes"
  // re-applies this, mirroring `baselineModel` for the short-term settings.
  private longerTermBaseline: LongerTermTargets = emptyLongerTermTargets();

  private async loadTargets(): Promise<void> {
    const addressId = this.selectedAddress.id;
    if (this.isDemo) {
      const targets = await this.rentalStrategy.getTargets(addressId);
      this.applyLongerTermTargets({
        midTarget: Math.round(targets.mid_monthly_target / 100),
        midFloor: Math.round(targets.mid_monthly_floor / 100),
        longTarget: Math.round(targets.long_monthly_target / 100),
        longFloor: Math.round(targets.long_monthly_floor / 100),
      });
      this.midRecommendedLabel = formatCents(summarizeComps(getComps(addressId, 'mid')).medianCents);
      this.longRecommendedLabel = formatCents(summarizeComps(getComps(addressId, 'long')).medianCents);
    } else {
      // No comps for real accounts, so there's nothing to recommend from.
      this.applyLongerTermTargets(this.readLongerTermTargets(addressId));
      this.midRecommendedLabel = '';
      this.longRecommendedLabel = '';
    }
    this.longerTermBaseline = { ...this.collectLongerTermTargets() };
    this.cdr.markForCheck();
  }

  /** Patch without emitting so hydrating an address never marks the page dirty. */
  private applyLongerTermTargets(targets: LongerTermTargets): void {
    this.targetForm.patchValue(targets, { emitEvent: false });
  }

  private collectLongerTermTargets(): LongerTermTargets {
    const v = this.targetForm.getRawValue();
    return {
      midTarget: numOrNull(v.midTarget),
      midFloor: numOrNull(v.midFloor),
      longTarget: numOrNull(v.longTarget),
      longFloor: numOrNull(v.longFloor),
    };
  }

  private longerTermStorageKey(addressId: number): string {
    return `optimizer-longer-term-${addressId}`;
  }

  private readLongerTermTargets(addressId: number): LongerTermTargets {
    try {
      const raw = localStorage.getItem(this.longerTermStorageKey(addressId));
      if (!raw) { return emptyLongerTermTargets(); }
      const parsed = JSON.parse(raw);
      return {
        midTarget: numOrNull(parsed?.midTarget),
        midFloor: numOrNull(parsed?.midFloor),
        longTarget: numOrNull(parsed?.longTarget),
        longFloor: numOrNull(parsed?.longFloor),
      };
    } catch {
      return emptyLongerTermTargets();
    }
  }

  private writeLongerTermTargets(addressId: number, targets: LongerTermTargets): void {
    try {
      localStorage.setItem(this.longerTermStorageKey(addressId), JSON.stringify(targets));
    } catch {
      // A full/blocked localStorage shouldn't sink the backend save alongside it.
    }
  }

  // ---- Listing optimization (mid/long channels) ----
  channelRows: ChannelListingRow[] = [];

  private refreshChannelRows(): void {
    this.channelRows = this.isDemo ? getChannelRows(this.selectedAddress.id) : emptyChannelRows();
    this.cdr.markForCheck();
  }

  // Single entry point called from ngOnInit, initRealAddresses and
  // onAddressChange — keeps every longer-term data source (comps, targets,
  // channels) in sync with whichever property is selected in the header picker.
  private async loadMidLongPipeline(): Promise<void> {
    this.refreshComps();
    await this.loadTargets();
    this.refreshChannelRows();
  }
}

/** The four editable longer-term rent figures, in dollars (null = not set). */
interface LongerTermTargets {
  midTarget: number | null;
  midFloor: number | null;
  longTarget: number | null;
  longFloor: number | null;
}

function emptyLongerTermTargets(): LongerTermTargets {
  return { midTarget: null, midFloor: null, longTarget: null, longFloor: null };
}

function defaultMonthRange(): string {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const now = new Date();
  const m = monthNames[now.getMonth()];
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return `${m} 1 - ${m} ${last}`;
}

function computeLastSixAmPacificMs(): number {
  const now = new Date();
  const ptParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const num = (type: string) => parseInt(ptParts.find(p => p.type === type)!.value, 10);
  const msIntoDayPT = (num('hour') * 3600 + num('minute') * 60 + num('second')) * 1000;
  const msSinceSixAmPT = msIntoDayPT >= 6 * 3600_000
    ? msIntoDayPT - 6 * 3600_000
    : msIntoDayPT + (24 - 6) * 3600_000;
  return now.getTime() - msSinceSixAmPT;
}

// Compact units stay as-is (h/m/d); the words go through the shared table the way the
// dashboard's own relative-time label does.
function formatRelativeAgo(ms: number): string {
  const t = (text: string) => new TranslationPipe().transform(text);
  const mins = Math.max(0, Math.floor(ms / 60_000));
  if (mins < 1) {return t('just now');}
  if (mins < 60) {return `${mins}m ${t('ago')}`;}
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) {return remMins > 0 ? `${hrs}h ${remMins}m ${t('ago')}` : `${hrs}h ${t('ago')}`;}
  const days = Math.floor(hrs / 24);
  return `${days}d ${t('ago')}`;
}
